/**
 * Token管理工具类
 * 统一管理token的验证、刷新等操作
 */

import urls from '@/api/api';
import request from '@/api/request';

/**
 * 检查token是否已过期
 */
export function isTokenExpired() {
  const tokenExpireTime = localStorage.getItem('token_expire_time');
  if (!tokenExpireTime) {
    return true;
  }
  
  const now = new Date().getTime();
  const expireTime = parseInt(tokenExpireTime);
  
  return now >= expireTime;
}

/**
 * 检查refresh token是否已过期
 */
export function isRefreshTokenExpired() {
  const refreshTokenExpireTime = localStorage.getItem('refresh_token_expire_time');
  if (!refreshTokenExpireTime) {
    return true;
  }
  
  const now = new Date().getTime();
  const expireTime = parseInt(refreshTokenExpireTime);
  
  return now >= expireTime;
}

/**
 * 检查token是否即将过期（小于指定天数）
 * @param {number} days - 天数，默认2天
 */
export function shouldRefreshToken(days = 2) {
  const tokenExpireTime = localStorage.getItem('token_expire_time');
  if (!tokenExpireTime) {
    return false;
  }
  
  const now = new Date().getTime();
  const expireTime = parseInt(tokenExpireTime);
  const daysInMs = days * 24 * 60 * 60 * 1000;
  
  // 如果距离过期时间小于指定天数，返回true
  return (expireTime - now) < daysInMs && (expireTime - now) > 0;
}

/**
 * 获取token
 */
export function getToken() {
  return localStorage.getItem('sntoken');
}

/**
 * 获取refresh token
 */
export function getRefreshToken() {
  return localStorage.getItem('refresh_token');
}

/**
 * 保存token信息
 * @param {Object} tokenData - token数据对象
 */
export function saveTokenInfo(tokenData) {
  const now = new Date().getTime();
  const tokenExpireTime = now + (tokenData.expires_in * 1000);
  const refreshTokenExpireTime = now + (tokenData.refresh_expires_in * 1000);
  
  localStorage.setItem('sntoken', tokenData.access_token);
  localStorage.setItem('refresh_token', tokenData.refresh_token);
  localStorage.setItem('expires_in', tokenData.expires_in);
  localStorage.setItem('token_expire_time', tokenExpireTime);
  localStorage.setItem('refresh_token_expire_time', refreshTokenExpireTime);
}

/**
 * 清除token信息
 */
export function clearTokenInfo() {
  localStorage.removeItem('sntoken');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('expires_in');
  localStorage.removeItem('token_expire_time');
  localStorage.removeItem('refresh_token_expire_time');
}

/**
 * 清除所有本地存储
 */
export function clearAllStorage() {
  localStorage.clear();
}

/**
 * 刷新token
 * @returns {Promise<boolean>} 刷新是否成功
 */
export async function refreshToken() {
  const refreshTokenValue = getRefreshToken();
  
  if (!refreshTokenValue) {
    console.log('Refresh token不存在');
    return false;
  }
  
  // 检查refresh token是否过期
  if (isRefreshTokenExpired()) {
    console.log('Refresh token已过期，需要重新登录');
    return false;
  }
  
  try {
    // 使用api.js中配置的接口和request.js的modeParams方法
    const params = {
      grant_type: 'refresh_token',
      client_id: 'web',
      client_secret: 'secret',
      refresh_token: refreshTokenValue
    };
    
    const response = await new request(urls.m().refreshToken, params).modePostForm();
    
    if (response.data.code === 200) {
      const data = response.data.data;
      
      // 保存新的token信息
      saveTokenInfo(data);
      
      console.log('Token刷新成功');
      return true;
    }
  } catch (error) {
    console.error('刷新token失败:', error);
    return false;
  }
  
  return false;
}

/**
 * 获取token剩余时间（毫秒）
 */
export function getTokenRemainingTime() {
  const tokenExpireTime = localStorage.getItem('token_expire_time');
  if (!tokenExpireTime) {
    return 0;
  }
  
  const now = new Date().getTime();
  const expireTime = parseInt(tokenExpireTime);
  const remaining = expireTime - now;
  
  return remaining > 0 ? remaining : 0;
}

/**
 * 获取token剩余天数
 */
export function getTokenRemainingDays() {
  const remainingMs = getTokenRemainingTime();
  return remainingMs / (1000 * 60 * 60 * 24);
}

/**
 * 格式化token过期时间
 */
export function formatTokenExpireTime() {
  const tokenExpireTime = localStorage.getItem('token_expire_time');
  if (!tokenExpireTime) {
    return '未知';
  }
  
  const expireTime = parseInt(tokenExpireTime);
  return new Date(expireTime).toLocaleString('zh-CN');
}

/**
 * 检查是否有有效的token
 */
export function hasValidToken() {
  const token = getToken();
  if (!token) {
    return false;
  }
  
  // 至少有一个token未过期
  return !isTokenExpired() || !isRefreshTokenExpired();
}

/**
 * 获取token状态信息（用于调试）
 */
export function getTokenStatus() {
  return {
    hasToken: !!getToken(),
    hasRefreshToken: !!getRefreshToken(),
    tokenExpired: isTokenExpired(),
    refreshTokenExpired: isRefreshTokenExpired(),
    shouldRefresh: shouldRefreshToken(),
    remainingDays: getTokenRemainingDays().toFixed(2),
    tokenExpireTime: formatTokenExpireTime()
  };
}

export default {
  isTokenExpired,
  isRefreshTokenExpired,
  shouldRefreshToken,
  getToken,
  getRefreshToken,
  saveTokenInfo,
  clearTokenInfo,
  clearAllStorage,
  refreshToken,
  getTokenRemainingTime,
  getTokenRemainingDays,
  formatTokenExpireTime,
  hasValidToken,
  getTokenStatus
};

